import { useRef, useState, type DragEvent } from 'react';
import { deleteInvitationPhoto, uploadInvitationPhoto } from '@/lib/weddingApi';

interface Props {
  photos: string[];
  ownerId: string;
  onChange: (photos: string[]) => void;
}

const MAX_PHOTOS = 30;

export default function PhotoForm({ photos, ownerId, onChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState('');

  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    setError('');

    const files = Array.from(fileList).filter((f) => f.type.startsWith('image/'));
    if (files.length === 0) {
      setError('이미지 파일만 올릴 수 있어요.');
      return;
    }
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) {
      setError(`사진은 최대 ${MAX_PHOTOS}장까지 올릴 수 있어요.`);
      return;
    }
    const accepted = files.slice(0, room);

    setBusy(true);
    try {
      const uploaded = await Promise.all(
        accepted.map((file) => uploadInvitationPhoto(ownerId, file)),
      );
      onChange([...photos, ...uploaded]);
    } catch (err) {
      setError(err instanceof Error ? err.message : '사진 업로드에 실패했어요.');
    } finally {
      setBusy(false);
      setProgress('');
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    void handleFiles(e.dataTransfer.files);
  };

  const remove = async (url: string) => {
    setError('');
    onChange(photos.filter((p) => p !== url));
    try {
      await deleteInvitationPhoto(url);
    } catch {
      /* storage cleanup is best-effort */
    }
  };

  const makeCover = (url: string) => {
    onChange([url, ...photos.filter((p) => p !== url)]);
  };

  const move = (url: string, dir: -1 | 1) => {
    const idx = photos.indexOf(url);
    const next = idx + dir;
    if (idx === -1 || next < 0 || next >= photos.length) return;
    const copy = [...photos];
    [copy[idx], copy[next]] = [copy[next], copy[idx]];
    onChange(copy);
  };

  return (
    <div className="ed-section">
      <div className="ed-section-head">
        <span className="step">
          <i className="ri-image-line" aria-hidden="true" />
        </span>
        <h2>사진</h2>
        <span className="hint">
          {photos.length} / {MAX_PHOTOS}장
        </span>
      </div>
      <p className="ed-desc">
        올린 사진 중 <strong>맨 앞 사진이 청첩장 대표 사진(표지)</strong>이 되고, 나머지는 갤러리로
        구성돼요. 사진을 클릭하면 순서를 바꾸거나 대표 사진으로 지정할 수 있어요.
      </p>

      <div
        className={`ed-upload ${dragging ? 'is-drag' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => !busy && inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
        }}
      >
        {busy ? (
          <>
            <i className="ri-loader-4-line spin-anim" aria-hidden="true" />
            <strong>{progress || '업로드 중…'}</strong>
          </>
        ) : (
          <>
            <i className="ri-add-circle-line" aria-hidden="true" />
            <strong>사진 올리기</strong>
            <span>여기로 끌어다 놓거나 클릭해서 선택하세요 · JPG · PNG · WEBP</span>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => void handleFiles(e.target.files)}
        />
      </div>

      {error ? (
        <div className="pf-alert err" role="alert" style={{ marginTop: 14 }}>
          <i className="ri-error-warning-line" aria-hidden="true" />
          <span>{error}</span>
        </div>
      ) : null}

      {photos.length > 0 ? (
        <div className="ed-photo-grid">
          {photos.map((url, i) => (
            <div className="ed-photo" key={url}>
              <img src={url} alt={`사진 ${i + 1}`} />
              {i === 0 ? <span className="cover-tag">대표 사진</span> : null}
              <div className="tools">
                {i !== 0 ? (
                  <button
                    type="button"
                    className="ed-icon-btn"
                    title="대표 사진으로 지정"
                    onClick={() => makeCover(url)}
                  >
                    <i className="ri-star-line" aria-hidden="true" />
                  </button>
                ) : null}
                <button
                  type="button"
                  className="ed-icon-btn"
                  title="앞으로"
                  disabled={i === 0}
                  onClick={() => move(url, -1)}
                >
                  <i className="ri-arrow-left-line" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="ed-icon-btn"
                  title="뒤로"
                  disabled={i === photos.length - 1}
                  onClick={() => move(url, 1)}
                >
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="ed-icon-btn danger"
                  title="삭제"
                  onClick={() => void remove(url)}
                >
                  <i className="ri-delete-bin-line" aria-hidden="true" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="pf-empty" style={{ padding: '28px 20px' }}>
          <i className="ri-image-2-line" aria-hidden="true" />
          <p className="pf-muted">아직 올린 사진이 없어요.</p>
        </div>
      )}
    </div>
  );
}